/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: utils-service.ts
 *
 * Created by randysorensen on 3/7/17.
 */

angular.module('prism-ui.components').service('UtilsService', function (
    this: JS5Object,
    _: JS5Object,
    $http: JS5Function,
    $q :JS5Object
) {

    //===============//
    // App Constants //
    //===============//

    this.constants = {
        MINUTES_PER_DAY: 60 * 24,
        WEEKDAYS: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        MONTHS: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October',
            'November', 'December'],
        EARLIEST_RECORD_YEAR: 2013,
        DEFAULT_FETCH_LIMIT: 25,
    };


    //================//
    // Time Functions //
    //================//

    this.minutesToWeekday = (minutes: number): string => {
        const weekdayIndex = Math.floor(minutes / this.constants.MINUTES_PER_DAY);
        return this.constants.WEEKDAYS[weekdayIndex];
    };

    this.timeToString = (hours: number, minutes: number): string => {
        let strHours = String(hours);
        let strMinutes = String(minutes);

        if (strHours.length === 1) {
            strHours = '0' + strHours;
        }
        if (strMinutes.length === 1) {
            strMinutes = '0' + strMinutes;
        }
        return strHours + ':' + strMinutes;
    };

    this.minutesToTimeOfDay = (scheduleMinutes: number): string => {
        const MINUTES_PER_DAY = 60 * 24;
        scheduleMinutes = scheduleMinutes % MINUTES_PER_DAY;
        const hours = String(Math.floor(scheduleMinutes / 60));
        const minutes = String(scheduleMinutes % 60);
        return this.timeToString(hours, minutes);
    };


    //==================//
    // String Functions //
    //==================//

    this.firstToLowerCase = (str: string): string => {
        return str[0].toLowerCase() + str.slice(1);
    };

    this.removeSpaces = (str: string): string => {
        return str.split(' ').join('');
    };


    //=============//
    // Comparators //
    //=============//

    //
    // Returns:
    //      < 0 if date0 is less (earlier) than date1
    //      > 0 if date0 is greater (later) than date1
    //      0 if date0 and date1 are equal
    //
    this.dateCompare = (date0: string, date1: string): number => {
        return Date.parse(date0) - Date.parse(date1);
    };

    //
    // Returns:
    //      < 0 if str0 is less than str1
    //      > 0 if str0 is greater than str1
    //      0 if str0 and str1 are equal
    //
    this.caseInsensitiveStringCompare = (str0: string, str1: string): number => {
        str0 = str0.toLowerCase();
        str1 = str1.toLowerCase();

        if (str0 < str1) {
            return -1;
        } else if (str0 > str1) {
            return 1;
        } else {
            return 0;
        }
    };

    // Deep-compare two objects, but don't compare properties with
    // annotated or mangled names.
    const compareBasicObjects = (obj0: JS5Object, obj1: JS5Object): number => {
        let ignoredPropertyNamePrefixes = ['$', '_'];

        let allKeys = _.union(_.keys(obj0), _.keys(obj1));
        return allKeys.reduce((accum: any, key: any) => {
            if (ignoredPropertyNamePrefixes.indexOf(key[0]) < 0) {
                return accum + this.compareBasic(obj0[key], obj1[key]);
            } else {
                return accum;
            }
        }, 0);
    };

    const compareBasicArrays = (obj0: JS5Object, obj1: JS5Object): number => {
        if (obj0.length !== obj1.length) {
            return -1;
        }
        if (_.differenceWith(obj0, obj1, (a: any, b: any) => {
            return this.compareBasic(a, b) === 0;
        }).length !== 0) {
            return -1;
        }
        return 0;
    };

    this.compareBasic = (item0: JS5Object, item1: JS5Object): number => {
        //
        // Compare two items. Returns 0 if equal, non-zero if not
        // equal.
        //
        // The distinctions of this compare function are as
        // follows:
        //
        //	* object properties starting with '_' or '$' are
        //	  ignored
        //	* arrays are compared in an order-agnostic fashion;
        //    that is, [1, 2, 3] is equal to [2, 3, 1]
        //

        const type = typeof item0;
        const typeOther = typeof item1;

        if (type !== typeOther) {
            return -1;
        }

        if (type === 'object') {
            if (item0 === null || item1 === null) {
                return item0 === item1 ? 0 : -1;
            }
            if (Array.isArray(item0)) {
                return compareBasicArrays(item0, item1);
            }
            return compareBasicObjects(item0, item1);
        } else {
            return item0 === item1 ? 0 : -1;
        }
    };


    //============//
    // File Names //
    //============//

    this.buildFilters = (fileExtensions: string[]): RegExp => {
        return new RegExp('\.(' + fileExtensions.join('|') + ')$', 'i');
    };

    this.filtersToPrettyString = (fileExtensions: string[]): string => {
        return _.chain(fileExtensions).toLower().split(',').uniq().compact().join(', ').value();
    }

    this.getFileName = (path: string): string => {
        let fileName = '';
        if (path) {
            fileName = _.chain(path).split('/').last().value();
        }
        return fileName;
    };

    this.getFileExtension = (path: string): string => {
        let fileExtension = '';
        if (path) {
            fileExtension = _.chain(path)
                .split('?')
                .first()
                .split('.')
                .last()
                .toLower()
                .value();
        }
        return fileExtension;
    };

    this.isFileNameValid = (fileName: string) => {
        let testResult = /^[a-zA-Z0-9_~]+([a-zA-Z0-9_~ \.\-])+$/.test(fileName);
        return testResult;
    };

    this.getPathSeparator = (): string => {
        return _.startsWith(_.lowerCase(window.navigator.platform), 'win') ? '\\' : '/';
    };

    this.getFileNameFromLocalPath = (filePath: string): string => {
        return _.chain(filePath).split(this.getPathSeparator()).last().value();
    };

    this.appendBaseFilename = (filename: string, toAppend: string): string => {
        let basename = _.chain(filename).split('.').slice(0, -1).join('.').value();
        let extension = _.chain(filename).split('.').last().value();
        return `${basename}${toAppend}.${extension}`;
    };


    //===============//
    // File Download //
    //===============//

    this.downloadFile = (uri: string, downloadFileName: string=null) => {
        console.info(`Downloading "${downloadFileName}"`);
        console.info(`URI: "${uri}"`);

        let downloadPromise;

        if (downloadFileName !== null) {
            downloadPromise = $http({
                // crude trick allowing file downloads to have a
                // custom filename
                url: uri,
                method: 'GET',
                responseType: 'blob'
            }).then((response: JS5Object) => {
                let downloadLink = document.createElement('a');
                downloadLink.href = URL.createObjectURL(response.data);
                downloadLink.download = downloadFileName;

                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
            });
        } else {
            window.open(uri);
            downloadPromise = $q.when(true);
        }

        return downloadPromise;
    };


    //=======//
    // Misc. //
    //=======//

    // KeyCode expression represents alphanumeric, return and delete keys
    this.canPerformSearchFromKeyCode = (event: JS5Object) => {

        return ((event.keyCode > 47 && event.keyCode < 58) ||
            (event.keyCode > 64 && event.keyCode < 91) ||
            (event.keyCode > 96 && event.keyCode < 123) ||
            event.keyCode == 13 ||
            event.keyCode == 8 ||
            event.keyCode == 0);
    };

    this.callIfNotNull = (fn: JS5Function): any => {
        if (fn) {
            return fn();
        } else {
            return false;
        }
    };

    this.hasData = (prop: any): boolean => {
        return (
            prop !== undefined &&
            prop !== null &&
            prop !== ''
        );
    };

    this.clampNumericProp = (object: JS5Object, prop: string, min: number, max: number): void => {
        if (object[prop] > max) {
            object[prop] = max;
        } else if (object[prop] < min) {
            object[prop] = min;
        }
        object[prop] = Math.floor(object[prop]);
    };

    this.convertToNumber = (object: JS5Object, propName: string): void => {
        if (!!object && object[propName] !== undefined) {
            object[propName] = Number(object[propName]);
        }
    };

    this.arrayToString = (array: JS5Object, delimiter: string, key: string): string => {
        let newListString = '';
        _.forEach(array, (item: any) => {
            if (_.isEmpty(item[key])) {
                return;
            }

            if (_.isEmpty(newListString) && !_.isEmpty(item[key])) {
                newListString += item[key];
            } else if (!_.isEmpty(item[key])) {
                if (newListString !== delimiter) {
                    newListString += delimiter + item[key];
                } else {
                    newListString += item[key];
                }
            }
        });
        return newListString;
    };

    this.stringToArray = (value: any, delimiter: string, key: string): JS5Object => {
        let items: JS5Object = [];
        if (_.isString(value)) {
            let stringItems = _.split(value, delimiter);
            _.forEach(stringItems, (str: string) => {
                items.push({[key]: str});
            });
        }
        return items;
    };

    // Transform First (Middle) Last name format to Last, First (Middle) name format.
    this.transformDisplayNameToSortName = (displayName: string) => {
        // If the display name is nil return an empty string
        if (_.isNil(displayName)) {
            return '';
        }

        // Take the display name and split it on the spaces
        let displayNameDelimited = _.split(displayName, ' ');

        if (_.size(displayNameDelimited) > 1) {
            // if the display name is more than one word take the last word (n) and then concatenate it with a
            // comma and then the rest of the words (0 to n - 1)
            return `${_.last(displayNameDelimited)}, ${_.join(_.initial(displayNameDelimited), ' ')}`;
        } else {
            // if the display name is not nil and is one word, return it unmodified
            return displayName;
        }
    };

    // takes in a string without the enclosing // and tests it. For example, /[A-Z]+s/ would be '[A-Z]+s'
    this.inputValidatorByRegExpString = ($event: JS5Object, currentValue: JS5Object, regexString: string) => {
        if ($event.key.length === 1) {
            let newValue = $event.key;
            if (!(new RegExp(regexString).test(newValue))) {
                $event.preventDefault();
            }
        }
    };


    //=================//
    // Domain Specific //
    //=================//

    //
    // Takes in the content type and returns the path friendly keyword for routing
    //
    //     Movie/Special   -> movies
    //     Series          -> series
    //     Ad/PreRoll      -> adsAndPreRolls
    //

    const contentTypeToPathMapping = {
        'Series': 'series',
        'Movie/Special': 'movies',
        'Ad/PreRoll': 'adsAndPreRolls'
    };

    this.getPathKeywordFromContentType = (contentType: string) => {
        return _.get(contentTypeToPathMapping, contentType);
    };

    //
    // External ingest types
    //
    // - watchFolder
    // - JSONFeed
    //

    this.externalIngestTypes = {
        watchFolder: {
            value: 'watchFolder',
            displayName: 'Watch Folder'
        },
        jsonFeed: {
            value: 'jsonFeed',
            displayName: 'JSON Feed'
        }
    };

});
